/**
 * TanStack Query hooks for `blacklist-ip-block-anonymizer-block`. Thin wrappers
 * over ./api. List/create/delete plus an on-demand `evaluate` mutation used
 * by the test-IP probe.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	blacklistIpBlockAnonymizerBlockQueryKeys,
	create,
	evaluate,
	list,
	remove,
	type BlacklistKind,
	type CreateBlacklistBody,
	type CreateBlacklistResponse,
	type EvaluateBlacklistBody,
	type EvaluateBlacklistResponse,
	type ListBlacklistResponse,
} from "./api";

export function useBlacklistIpBlockAnonymizerBlockList(params?: {
	kind?: BlacklistKind;
	offer_id?: string | "none";
	limit?: number;
}): UseQueryResult<ListBlacklistResponse, Error> {
	return useQuery({
		queryKey: [
			...blacklistIpBlockAnonymizerBlockQueryKeys.list(),
			params?.kind ?? null,
			params?.offer_id ?? null,
			params?.limit ?? null,
		],
		queryFn: () => list(params),
	});
}

export function useBlacklistIpBlockAnonymizerBlockCreate(): UseMutationResult<
	CreateBlacklistResponse,
	Error,
	CreateBlacklistBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: blacklistIpBlockAnonymizerBlockQueryKeys.all,
			});
		},
	});
}

export function useBlacklistIpBlockAnonymizerBlockDelete(): UseMutationResult<
	{ ok: true },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: blacklistIpBlockAnonymizerBlockQueryKeys.all,
			});
		},
	});
}

export function useBlacklistIpBlockAnonymizerBlockEvaluate(): UseMutationResult<
	EvaluateBlacklistResponse,
	Error,
	EvaluateBlacklistBody
> {
	return useMutation({
		mutationFn: evaluate,
	});
}
